import { useNavigate } from 'react-router';

import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiOrgId,
  ApiVolopId,
} from '#/api/types/ApiTypedId';
import { Button } from '#/components/Button/Button';
import { useI18n } from '#/i18n/hooks/useI18n';
import { OrgDashboardHideListingContainer } from '#/modules/orgDashboard/containers/HideListing/OrgDashboardHideListingContainer';
import { orgDashboardGetViewListingPathSwitch } from '#/modules/orgDashboard/utils/orgDashboardGetViewListingPathSwitch';
import type { ListingType } from '#/types/ListingType';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { navigationFlash } from '#/zustand-stores/flashesStore';

type Props = {
  orgId: ApiOrgId;
  listingId: ApiJobId | ApiInternshipId | ApiVolopId | ApiEventId;
  listingType: Exclude<ListingType, 'ORG'>;
  canPost: boolean;
  hasAts: boolean;
  isPreview?: boolean;
};

export function ListingViewAdminTools({
  orgId,
  listingId,
  listingType,
  canPost,
  hasAts,
  isPreview,
}: Props) {
  const { i18n } = useI18n();

  const navigate = useNavigate();

  const viewUrl = orgDashboardGetViewListingPathSwitch({
    orgId,
    listingId,
    listingType,
  });

  return (
    <>
      <Button
        type="button"
        onClick={() => {
          trackClicked('Edit Listing', { context: 'listing' });
          navigate(`${viewUrl}/edit`);
        }}
        qaId="edit-button"
        size="small"
        variant="secondary-white"
        disabled={!canPost}
      >
        {i18n('Edit')}
      </Button>
      <OrgDashboardHideListingContainer
        listingId={listingId}
        listingType={listingType}
        orgId={orgId}
        render={({ hideListing, submitting }) => (
          <Button
            type="button"
            onClick={() => {
              trackClicked('Hide Listing', { context: 'listing' });

              hideListing().then(() => {
                if (!isPreview) {
                  navigationFlash(i18n('Your listing has been hidden.'));
                  navigate(viewUrl);
                }
              });
            }}
            disabled={submitting}
            qaId="hide-button"
            size="small"
            variant="secondary-white"
          >
            {
              /* TRANSLATORS: hide listing, button text */
              i18n('Hide')
            }
          </Button>
        )}
      />
      <Button
        type="button"
        onClick={() => {
          trackClicked('Repost Listing', { context: 'listing' });
          navigate(`${viewUrl}/repost`);
        }}
        qaId="repost-button"
        variant="secondary-white"
        size="small"
        disabled={!canPost}
      >
        {i18n('Repost')}
      </Button>
      {hasAts && (
        <Button
          type="link"
          href={viewUrl}
          size="small"
          variant="secondary-white"
        >
          {i18n('View Applicants')}
        </Button>
      )}
    </>
  );
}
